import React, { useState } from 'react';
import TableFunction from '../../../../../components/table/TableFunction';
import CustomImageTable from '../../../../../components/CustomImageTable';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import { SIOS_TYPE } from '../../SIOs/Model/Models';
import { PROTOCOL_DATA, READER_MODE_DATA, READER_NUMBER_DATA } from '../Model/Models';
import {EMPTY_VALUES, errorMsg, getValueByKey} from "../../../../../Apis/functions";
import { SimpleSearch } from "../../../../../reduxToolkit/Search/SearchApi";
import { TABLES } from "../../../../../Apis/Tables";
import {
    DEFAULT_LEFT,
    LEFT_FIRST_COLUMN_WITH_CHECKBOX,
    SEARCH_MODEL
} from "../../../../../components/table/Model/TableModel";
import { useDispatch, useSelector } from "react-redux";
import { permissionObj } from "../../../../../Helpers/permission";
import { getDeviceAccessTypeById, getIconClassesContainer } from "../../../Devices/Model/Model";
import {
    CheckIconsOptions,
    CrossIconsOptions
} from "../../../../../components/buttons/IconButtons";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";
import CustomTooltip from '../../../../../components/CustomTooltip';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 * MainReaderTableApp
 *
 * React hook-based component that manages the HID Reader table.
 * It handles data fetching, pagination, deletion, and permission-based actions.
 */
const MainReaderTableApp = ({ selectedList, setSelectedList }) => {
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const dispatch = useDispatch();

    // Local states
    const tableId = "tbl_mrt_table";
    const searchId = "txt_mrt_search";
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [results, setResults] = useState([]);
    const [search, setSearch] = useState(false);
    const [searchElement, setSearchElement] = useState('');
    const [bodyForExport, setBodyForExport] = useState('');
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [modified, setModified] = useState(false);

    const option = TABLES.HID_READER;
    const deletePermission = (DEV || permission?.includes(permissionObj?.WEB_HID_DELETE));
    const leftPosition = deletePermission ? LEFT_FIRST_COLUMN_WITH_CHECKBOX : DEFAULT_LEFT;

    /**
     * Builds the table header (thead) dynamically.
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} classes="first_head" fixedXY={true} left={leftPosition}>{t('name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('reader_number')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('access_type')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('SIO')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('key_pad')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('communication_protocol')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('reader_mode')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('keep_open')}</CustomTh>
        ];
    };

    /**
     * Builds the table body (tbody) using API results.
     * Each row displays reader details such as number, access type, and mode.
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return { body: [], data: [] };

        let iconClassContainer = getIconClassesContainer(results?.content || []);
        let body = results?.content?.map((item) => {
            let deviceAccessType = getDeviceAccessTypeById(item?.deviceAccessTypeId);

            return (
                <>
                    <CustomTd tableId={tableId} classes={'first-column'} fixed={true} left={leftPosition}>
                        <CustomTooltip
                            text={item?.name || EMPTY_VALUES.DEFAULT}
                            classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {t(getValueByKey(item?.readerNo, 'name', READER_NUMBER_DATA))}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomImageTable
                            children={<span className='text-ellipsis text-left'>{t(deviceAccessType?.trans)}</span>}
                            tooltip={t(deviceAccessType?.trans)}
                            containerClass={'d-flex align-items-center'}
                            icon={
                                <div className={`${iconClassContainer} mr-2 ${deviceAccessType.color}`}>
                                    <deviceAccessType.Icon />
                                </div>
                            }
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>{t(getValueByKey(item?.sioType, 'name', SIOS_TYPE))}</CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomImageTable image={item?.hasKeyPad ? CheckIconsOptions : CrossIconsOptions} />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {getValueByKey(item?.communicationProtocol, 'name', PROTOCOL_DATA)}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {t(getValueByKey(item?.readerMode, 'name', READER_MODE_DATA)) || EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>{item?.keepOpen || EMPTY_VALUES.DEFAULT}</CustomTd>
                </>
            );
        });

        return { body, data: results?.content };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, key:searchElement });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    // Table configuration and handlers from TableFunction helper
    const { t, thead, tbody, handleCheckboxChange, handleDeleteAll, pagination, page, rowsPerPage } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getThead,
        getTbody,
        labelRowsPerPage: 'reader_per_page',
        setIsAllChecked,

        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Deletes selected readers using the HID API.
     */
    const deleteSelectedReader = () => {
        const body = selectedList;
        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName: TABLES.HID_READER, body })).then(
                ({ payload: { data: { success, code } } }) => {
                    if (success === true) {
                        setSelectedList([]);
                        setIsAllChecked(false);
                        setModified(true)
                    }else errorMsg(code);
                },
            );
        }
    };

    // Expose table data, handlers, and states to parent components
    return {
        t,
        DEV,
        permission,
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
        page,
        rowsPerPage,
        setResults,
        option,
        searchElement,
        setSearchElement,
        setSearch,
        setBodyForExport,
        setSimpleSearch,
        deletePermission,
        isAllChecked,
        setModified,
        handleCheckboxChange,
        handleDeleteAll,
        deleteSelectedReader
    };
};

export default MainReaderTableApp;
